// combatpro/treasure/treasureEngine.js
//
// The data-driven Treasure generator. All behaviour comes from the loaded catalogue
// (items + loot profiles + currency + presets) plus the request; there are NO
// hard-coded per-creature or per-theme rules here. Given the same seed and catalogue
// it is deterministic. Graceful fallback guarantees it never throws on an empty pool.
//
// No DOM, no network, no optional chaining, no em dash. Pure functions only.

import {
  RARITY_BASE_WEIGHT, WEALTH_RARITY_BOOST, WEALTH_RANK, QUANTITY_PICKS,
  COIN_BASE_PER_LEVEL, WEALTH_COIN_MULT, clampLevel
} from './treasureVocab';

// ---- RNG (mulberry32) - deterministic when seeded ---------------------------
export function makeRng(seed) {
  if (seed === undefined || seed === null || seed === '') {
    return function () { return Math.random(); };
  }
  var a = 0;
  var str = String(seed);
  for (var i = 0; i < str.length; i++) { a = (a + str.charCodeAt(i) * (i + 1)) | 0; }
  a = (a ^ 0x9e3779b9) | 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    var t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function intersects(a, b) {
  if (!a || !b || !a.length || !b.length) { return false; }
  for (var i = 0; i < a.length; i++) { if (b.indexOf(a[i]) !== -1) { return true; } }
  return false;
}

// ---- Profile resolution -----------------------------------------------------
// Merge base + mode + creatureType/role + environment + theme profiles into one
// effective config. Later (higher priority) profiles layer over earlier ones.
export function resolveConfig(catalogue, ctx) {
  var pbk = (catalogue && catalogue.profilesByKind) ? catalogue.profilesByKind : {};
  var cfg = { categoryWeights: {}, categoryExclude: {}, coins: { enabled: true, multiplier: 1, weights: null }, rarityBias: {}, requireTags: [] };

  function applyProfile(p) {
    if (!p) { return; }
    // categoryWeights REPLACES the accumulated distribution (a creature/mode profile
    // fully defines its own loot spread). Profiles that only tweak use rarityBias / coins.
    if (p.categoryWeights) { cfg.categoryWeights = {}; Object.keys(p.categoryWeights).forEach(function (c) { cfg.categoryWeights[c] = p.categoryWeights[c]; }); }
    if (Array.isArray(p.categoryExclude)) { p.categoryExclude.forEach(function (c) { cfg.categoryExclude[c] = true; }); }
    if (p.coins) {
      if (p.coins.enabled === false) { cfg.coins.enabled = false; }
      if (typeof p.coins.multiplier === 'number') { cfg.coins.multiplier = cfg.coins.multiplier * p.coins.multiplier; }
      if (p.coins.weights) { cfg.coins.weights = p.coins.weights; }
    }
    if (p.rarityBias) { Object.keys(p.rarityBias).forEach(function (r) { cfg.rarityBias[r] = (cfg.rarityBias[r] || 1) * p.rarityBias[r]; }); }
    if (Array.isArray(p.requireTags)) { cfg.requireTags = cfg.requireTags.concat(p.requireTags); }
  }

  function pick(kind, matchTags) {
    var list = (pbk[kind] || []).slice();
    // profiles whose match is empty apply to all; otherwise must intersect matchTags
    list = list.filter(function (p) { return !p.match || p.match.length === 0 || intersects(p.match, matchTags || []); });
    list.sort(function (a, b) { return (a.priority || 0) - (b.priority || 0); });
    list.forEach(applyProfile);
  }

  pick('base', []);
  if (ctx.mode === 'hoard') { pick('hoard', []); }
  if (ctx.mode === 'container') { pick('container', []); }
  if (ctx.mode === 'reward') { pick('reward', []); }
  pick('creatureType', ctx.creatureTags || []);
  pick('creatureRole', ctx.creatureTags || []);
  pick('environment', ctx.environment ? [ctx.environment] : []);
  pick('theme', ctx.theme ? [ctx.theme] : []);

  // Hard excludes win over any weight.
  Object.keys(cfg.categoryExclude).forEach(function (c) { delete cfg.categoryWeights[c]; });
  return cfg;
}

// ---- Composition ------------------------------------------------------------
export function normalizeComposition(comp) {
  var out = {};
  var total = 0;
  Object.keys(comp || {}).forEach(function (k) { var v = Number(comp[k]) || 0; if (v > 0) { out[k] = v; total += v; } });
  if (total <= 0) { return {}; }
  Object.keys(out).forEach(function (k) { out[k] = (out[k] / total) * 100; });
  return out;
}

function compositionFromWeights(weights, exclude) {
  var comp = {};
  Object.keys(weights || {}).forEach(function (c) {
    if (exclude && exclude[c]) { return; }
    if (weights[c] > 0) { comp[c] = weights[c]; }
  });
  return normalizeComposition(comp);
}

// ---- Item eligibility + weighting -------------------------------------------
function itemEligible(item, ctx) {
  if (!item) { return false; }
  var lvl = ctx.level;
  if (typeof item.minLevel === 'number' && lvl < item.minLevel) { return false; }
  if (typeof item.maxLevel === 'number' && lvl > item.maxLevel) { return false; }
  if (!ctx.ignoreWealth) {
    var itRank = WEALTH_RANK[item.wealthTier]; var reqRank = WEALTH_RANK[ctx.wealth];
    if (typeof itRank === 'number' && typeof reqRank === 'number' && itRank > reqRank) { return false; }
  }
  if (ctx.creatureTags && ctx.creatureTags.length) {
    if (intersects(item.creatureExclude, ctx.creatureTags)) { return false; }
    if (item.creatureTags && item.creatureTags.length && !intersects(item.creatureTags, ctx.creatureTags)) {
      if (!ctx.relaxCreature) { return false; }
    }
  }
  if (ctx.requireTags && ctx.requireTags.length && !ctx.relaxRequire) {
    var all = [].concat(item.creatureTags || [], item.themeTags || [], item.environmentTags || [], item.sourceTags || []);
    if (!intersects(ctx.requireTags, all)) { return false; }
  }
  return true;
}

function itemWeight(item, ctx, rarityBias) {
  var rw = (RARITY_BASE_WEIGHT[item.rarity] || 1);
  var wb = WEALTH_RARITY_BOOST[ctx.wealth] ? (WEALTH_RARITY_BOOST[ctx.wealth][item.rarity] || 1) : 1;
  var bias = rarityBias && rarityBias[item.rarity] ? rarityBias[item.rarity] : 1;
  var w = (item.weight || 1) * rw * wb * bias;
  // soft context boosts (never zero the item out)
  if (ctx.environment && intersects(item.environmentTags, [ctx.environment])) { w *= 1.6; }
  if (ctx.theme && intersects(item.themeTags, [ctx.theme])) { w *= 1.6; }
  if (ctx.container && intersects(item.sourceTags, [ctx.container])) { w *= 1.6; }
  return w;
}

function weightedPick(list, weightFn, rng) {
  var total = 0; var i;
  for (i = 0; i < list.length; i++) { total += Math.max(0, weightFn(list[i])); }
  if (total <= 0) { return null; }
  var r = rng() * total;
  for (i = 0; i < list.length; i++) { r -= Math.max(0, weightFn(list[i])); if (r <= 0) { return list[i]; } }
  return list[list.length - 1];
}

// Pick one item from a category, trying progressively relaxed contexts before giving up.
function pickItemFromCategory(catalogue, cat, ctx, rarityBias, chosenUnique, rng) {
  var pool = (catalogue.byCategory[cat] || []);
  if (!pool.length) { return null; }
  var relaxations = [ {}, { ignoreWealth: true }, { ignoreWealth: true, relaxRequire: true }, { ignoreWealth: true, relaxRequire: true, relaxCreature: true } ];
  for (var r = 0; r < relaxations.length; r++) {
    var rctx = Object.assign({}, ctx, relaxations[r]);
    var eligible = pool.filter(function (it) {
      if (it.unique && chosenUnique[it.id]) { return false; }
      return itemEligible(it, rctx);
    });
    if (eligible.length) {
      var pickCtx = rctx;
      var chosen = weightedPick(eligible, function (it) { return itemWeight(it, pickCtx, rarityBias); }, rng);
      if (chosen) { return chosen; }
    }
  }
  return null;
}

// ---- Coins ------------------------------------------------------------------
function generateCoins(catalogue, ctx, cfg, coinWeightsOverride, rng) {
  var currency = catalogue.currency || { base: 'cp', denominations: [] };
  var denoms = currency.denominations || [];
  if (!denoms.length) { return { counts: {}, value: 0 }; }
  var lvl = Math.max(1, ctx.level);
  var totalValue = COIN_BASE_PER_LEVEL * lvl * (WEALTH_COIN_MULT[ctx.wealth] || 1) * (cfg.coins.multiplier || 1);
  totalValue = totalValue * (0.75 + rng() * 0.5); // +/- variance
  // choose weights: override > profile > currency.autoByLevel > equal
  var weights = coinWeightsOverride;
  if (!weights) { weights = cfg.coins.weights; }
  if (!weights && Array.isArray(currency.autoByLevel)) {
    for (var b = 0; b < currency.autoByLevel.length; b++) {
      var band = currency.autoByLevel[b];
      if (lvl >= band.minLevel && lvl <= band.maxLevel) { weights = band.weights; break; }
    }
  }
  if (!weights) { weights = {}; denoms.forEach(function (d) { weights[d.id] = 1; }); }
  var wsum = 0; denoms.forEach(function (d) { wsum += Math.max(0, weights[d.id] || 0); });
  if (wsum <= 0) { denoms.forEach(function (d) { weights[d.id] = 1; }); wsum = denoms.length; }
  var counts = {}; var realized = 0;
  denoms.forEach(function (d) {
    var share = (Math.max(0, weights[d.id] || 0) / wsum) * totalValue;
    var n = Math.floor(share / d.value);
    if (n > 0) { counts[d.id] = n; realized += n * d.value; }
  });
  return { counts: counts, value: Math.round(realized) };
}

// Generate a single item from ONE category (used by per-item reroll). Returns a
// result-item shape { id, name, category, qty, value, desc, rarity, magic } or null.
export function generateCategoryItem(catalogue, request, category, seedOverride) {
  request = request || {};
  var rng = makeRng(seedOverride !== undefined ? seedOverride : (request.seed || Date.now()));
  var ctx = {
    mode: request.mode || 'individual', wealth: request.wealth || 'standard', level: clampLevel(request.level === undefined ? 1 : request.level),
    creatureTags: request.creatureTags || [], environment: request.environment || '', theme: request.theme || '', container: request.container || ''
  };
  var cfg = resolveConfig(catalogue, ctx);
  ctx.requireTags = cfg.requireTags;
  var item = pickItemFromCategory(catalogue, category, ctx, cfg.rarityBias, {}, rng);
  if (!item) { return null; }
  var qty = 1;
  if (item.quantity && typeof item.quantity.min === 'number' && typeof item.quantity.max === 'number' && item.quantity.max >= item.quantity.min) {
    qty = item.quantity.min + Math.floor(rng() * (item.quantity.max - item.quantity.min + 1));
  }
  return { id: item.id, name: item.name, category: item.category, desc: item.desc || '', rarity: item.rarity, magic: !!item.magic, unitValue: item.value || 0, qty: qty, value: Math.round((item.value || 0) * qty) };
}

// ---- Main -------------------------------------------------------------------
export function generateTreasure(catalogue, request, seedOverride) {
  request = request || {};
  var effectiveSeed = seedOverride !== undefined ? seedOverride : request.seed;
  var rng = makeRng(effectiveSeed);
  var mode = request.mode || 'individual';
  var wealth = request.wealth || 'standard';
  var quantity = request.quantity || 'normal';
  var level = clampLevel(request.level === undefined ? 1 : request.level);
  var ctxBase = {
    mode: mode, wealth: wealth, level: level,
    creatureTags: request.creatureTags || [],
    environment: request.environment || '', theme: request.theme || '', container: request.container || ''
  };

  var cfg = resolveConfig(catalogue, ctxBase);
  ctxBase.requireTags = cfg.requireTags;

  // Composition: explicit user % override, else derived from profile weights.
  var comp;
  if (request.composition && Object.keys(request.composition).length) {
    comp = normalizeComposition(request.composition);
    Object.keys(cfg.categoryExclude).forEach(function (c) { delete comp[c]; });
    comp = normalizeComposition(comp);
  } else {
    comp = compositionFromWeights(cfg.categoryWeights, cfg.categoryExclude);
  }

  var coinsShare = comp.coins || 0;
  var coinsEnabled = cfg.coins.enabled && coinsShare > 0;

  // Item composition = everything except coins, renormalized.
  var itemComp = {};
  Object.keys(comp).forEach(function (c) { if (c !== 'coins') { itemComp[c] = comp[c]; } });
  itemComp = normalizeComposition(itemComp);

  var qpick = QUANTITY_PICKS[quantity] || QUANTITY_PICKS.normal;
  var picks = (quantity === 'custom' && typeof request.customPicks === 'number')
    ? Math.max(0, request.customPicks)
    : (qpick[mode] || qpick.individual || 2);

  var catList = Object.keys(itemComp);
  var chosenUnique = {};
  var agg = {}; // id -> aggregated item

  for (var p = 0; p < picks; p++) {
    if (!catList.length) { break; }
    var cat = weightedPick(catList, function (c) { return itemComp[c]; }, rng);
    if (!cat) { break; }
    var item = pickItemFromCategory(catalogue, cat, ctxBase, cfg.rarityBias, chosenUnique, rng);
    if (!item) { continue; }
    if (item.unique) { chosenUnique[item.id] = true; }
    var qty = 1;
    if (item.quantity && typeof item.quantity.min === 'number' && typeof item.quantity.max === 'number' && item.quantity.max >= item.quantity.min) {
      qty = item.quantity.min + Math.floor(rng() * (item.quantity.max - item.quantity.min + 1));
    }
    if (agg[item.id]) { agg[item.id].qty += qty; }
    else { agg[item.id] = { id: item.id, name: item.name, category: item.category, desc: item.desc || '', rarity: item.rarity, magic: !!item.magic, unitValue: item.value || 0, qty: qty }; }
  }

  var items = Object.keys(agg).map(function (k) { var it = agg[k]; it.value = Math.round(it.unitValue * it.qty); return it; });
  var coins = coinsEnabled ? generateCoins(catalogue, ctxBase, cfg, request.coinComposition, rng) : { counts: {}, value: 0 };

  var totalValue = coins.value;
  items.forEach(function (it) { totalValue += it.value; });

  return {
    mode: mode, level: level, wealth: wealth, quantity: quantity,
    context: { creatureTags: ctxBase.creatureTags, environment: ctxBase.environment, theme: ctxBase.theme, container: ctxBase.container },
    coins: coins,
    items: items,
    totalValue: Math.round(totalValue),
    composition: comp,
    seed: (effectiveSeed === undefined ? null : effectiveSeed),
    // Generation is a pure fixed-seed operation. Interactive callers use a
    // millisecond timestamp as their seed, so it also remains the real creation
    // time without consulting the wall clock a second time.
    generatedAt: (typeof request.generatedAt === 'number')
      ? request.generatedAt
      : (typeof effectiveSeed === 'number' ? effectiveSeed : 0)
  };
}
